import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _partition from 'lodash/partition';

import AttributeSelector from 'components/attributeSelector/Local';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import ReorderIcon from '@material-ui/icons/Reorder';
import ArrowUp from '@material-ui/icons/ArrowUpward';
import ArrowDown from '@material-ui/icons/ArrowDownward';
import Typography from '@material-ui/core/Typography';

import Field from '../Field';
import CategoryCardChild from 'pages/ProfileLayout/components/CategoryCard';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';

// Style
import withStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';

const SortableHandleElem = SortableHandle(({ className }) => <ReorderIcon className={className} />);

const SortableList = SortableContainer(
  ({ category, handleChange, onLayoutChange, getValidAttributes, removeSection, typeKey }) => {
    if (category.value.children === undefined) {
      return <Typography>No Sub Fields / Categories</Typography>;
    }
    return (
      <div>
        {category.value.children.map((value, index) => {
          return (
            <SortableItemElem
              key={`child-${index}-${value.value.id}-${value.type}`}
              index={index}
              value={value}
              handleChange={handleChange}
              getValidAttributes={getValidAttributes}
              removeSection={removeSection}
              category={category}
              layout={category.value.children}
              onLayoutChange={onLayoutChange}
              itemIndex={index}
              typeKey={typeKey}
            />
          );
        })}
      </div>
    );
  },
);

const SortableItemElem = SortableElement(
  ({
    key,
    layout,
    value,
    handleChange,
    category,
    onLayoutChange,
    getValidAttributes,
    removeSection,
    itemIndex,
    typeKey,
  }) => {
    if (value.type === 'field') {
      return (
        <div>
          <Field
            attribute={value}
            parentCategory={category}
            useDragHandle
            removeSection={removeSection}
          />
        </div>
      );
    } else if (value.type === 'category') {
      return (
        <div>
          <CategoryCardChild
            typeKey={typeKey}
            category={value}
            secondLevelCategory
            getValidAttributes={getValidAttributes}
            handleChange={handleChange}
            onLayoutChange={onLayoutChange}
            useDragHandle
            removeSection={removeSection}
            layout={layout}
            itemIndex={itemIndex}
          />
        </div>
      );
    }
  },
);

class CategoryCard extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    category: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    getValidAttributes: PropTypes.func.isRequired,
    entityType: PropTypes.object.isRequired,
    attributeTempValue: PropTypes.object.isRequired,
    removeSection: PropTypes.func.isRequired,
    secondLevelCategory: PropTypes.bool,
    onLayoutChange: PropTypes.func.isRequired,
    layout: PropTypes.array.isRequired,
    itemIndex: PropTypes.number.isRequired,
    typeKey: PropTypes.string.isRequired,
  };

  state = {
    open: false,
    availableAttributes: [],
    collapse: true,
  };

  componentDidMount() {
    const availableAttributes = this.props.getValidAttributes(this.props.category.name);
    this.setState({ availableAttributes });
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    let updatedCategory = _cloneDeep(this.props.category);
    const updatedChildren = arrayMove(updatedCategory.value.children, oldIndex, newIndex);
    updatedCategory.value.children = updatedChildren;
    this.props.handleChange(updatedCategory);
  };

  getAttributeKey(attribute) {
    return attribute.id;
  }

  getAttributeDisplayName(attribute) {
    return attribute.displayName;
  }

  handleDown = () => {
    const { itemIndex, onLayoutChange } = this.props;
    onLayoutChange({ oldIndex: itemIndex, newIndex: itemIndex + 1 });
  };

  handleUp = () => {
    const { itemIndex, onLayoutChange } = this.props;
    onLayoutChange({ oldIndex: itemIndex, newIndex: itemIndex - 1 });
  };

  saveAttributes = attributes => {
    const attributeNames = Object.keys(attributes);
    const { entityType, attributeTempValue, category, handleChange } = this.props;
    let attributeObjects = [];
    let tempCategory = _cloneDeep(category);
    const fieldKeys = Object.keys(entityType._fields);
    fieldKeys.forEach(key => {
      attributeNames.forEach(attributeName => {
        if (entityType._fields[key].id === attributeName) {
          attributeObjects.push(entityType._fields[key]);
        }
      });
    });
    attributeObjects.forEach(object => {
      const attributeTemplate = _cloneDeep(attributeTempValue);
      attributeTemplate.name = object.objectKey;
      attributeTemplate.type = 'field';
      attributeTemplate.value.id = object.id;
      attributeTemplate.value.displayName = object.displayName;
      attributeTemplate.value.entityField = object.entityField;
      attributeTemplate.value.restricted = object.restricted;
      attributeTemplate.value.children = undefined;
      const createdAttribute = attributeTemplate;
      tempCategory.value.children.push(createdAttribute);
    });
    handleChange(tempCategory);
  };

  handleRemoveClick = () => {
    this.setState({ collapse: false });
  };

  handleOnExited = () => {
    this.props.removeSection(this.props.category.name);
  };

  handleRemoveChildClick = childCategoryId => {
    let newCategory = _cloneDeep(this.props.category);
    const removeIndex = newCategory.value.children.findIndex(
      child => child.name === childCategoryId,
    );
    newCategory.value.children.splice(removeIndex, 1);
    this.props.handleChange(newCategory);
  };

  handleChildChange = updatedCategory => {
    let newUpdatedCategory = _cloneDeep(this.props.category);
    const childIndex = newUpdatedCategory.value.children.findIndex(
      child => child.name === updatedCategory.name,
    );
    newUpdatedCategory.value.children[childIndex] = updatedCategory;
    this.props.handleChange(newUpdatedCategory);
  };

  reorderList = () => {
    const reducedAttributes = this.state.availableAttributes.reduce((acc, value) => {
      const valueAlreadySelected = this.props.category.value.children.find(
        child => value.id === child.value.id,
      );
      if (!valueAlreadySelected) {
        acc.push(value);
      }
      return acc;
    }, []);

    const [requiredItems, nonRequiredItems] = _partition(reducedAttributes, function(o) {
      return _get(o, 'required', false);
    });

    nonRequiredItems.sort((a, b) =>
      a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()),
    );
    return requiredItems.concat(nonRequiredItems);
  };

  displayRemoveButton = () => {
    const { classes } = this.props;

    return (
      <IconButton
        aria-label="remove"
        classes={{ root: classes.removeCircleContainer }}
        onClick={this.handleRemoveClick}
      >
        <RemoveCircleOutlineIcon className={classes.removeCircleOutlineIcon} />
      </IconButton>
    );
  };

  render() {
    const { classes, itemIndex, layout, category, secondLevelCategory, typeKey } = this.props;

    const childrenAreAllFields = !_get(category, 'value.children', []).some(
      child => child.type === 'category',
    );

    return (
      <Collapse in={this.state.collapse} timeout={250} onExited={this.handleOnExited}>
        <div className={classes.root}>
          <div className={classes.iconContainer}>
            <SortableHandleElem className={classes.dragHandleIcon} />
            {layout && itemIndex !== 0 && (
              <ArrowUp onClick={this.handleUp} className={classes.arrowIcon} />
            )}
            {layout && itemIndex !== layout.length - 1 && (
              <ArrowDown onClick={this.handleDown} className={classes.arrowIcon} />
            )}
          </div>
          <Card
            className={classNames(classes.card, {
              [classes.secondLevelCategory]: secondLevelCategory,
            })}
            classes={{ root: classes.cardRoot }}
          >
            <CardHeader
              classes={{
                root: classNames(classes.header, {
                  [classes.secondLevelCategoryHeader]: secondLevelCategory,
                }),
                action: classes.action,
              }}
              title={
                <Typography variant={secondLevelCategory ? 'h6' : 'h5'}>
                  {category.value.displayName}
                </Typography>
              }
              action={
                <div className={classes.align}>
                  {childrenAreAllFields && (
                    <AttributeSelector
                      label="Add Attributes"
                      title="Add Profile Attributes"
                      open={this.state.open}
                      attributes={this.reorderList()}
                      selectedAttributes={{}}
                      searchPlaceholder="Search attributes"
                      getAttributeKey={this.getAttributeKey}
                      getAttributeDisplayName={this.getAttributeDisplayName}
                      onSaveSelectedAttributes={this.saveAttributes}
                      classes={{
                        root: classes.attributeSelectorRoot,
                        attributeSelector: classes.attributeSelector,
                        attributes: classes.attributesList,
                      }}
                      LabelButtonProps={{
                        color: 'secondary',
                      }}
                    />
                  )}
                  {this.displayRemoveButton()}
                </div>
              }
            />
            <CardContent
              className={classes.cardContainer}
              classes={{ root: classes.categoryCardContent }}
            >
              <div>
                <SortableList
                  currentLayout={this.props.category.children}
                  onSortEnd={this.onSortEnd}
                  onLayoutChange={this.onSortEnd}
                  useDragHandle
                  getChildField={this.getChildField}
                  getChildCategory={this.getChildCategory}
                  handleChange={this.handleChildChange}
                  getValidAttributes={this.props.getValidAttributes}
                  category={category}
                  removeSection={this.handleRemoveChildClick}
                  typeKey={typeKey}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </Collapse>
    );
  }
}

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing(2),
    '-webkit-touch-callout': 'none' /* iOS Safari */,
    '-webkit-user-select': 'none' /* Safari */,
    ' -khtml-user-select': 'none' /* Konqueror HTML */,
    '-moz-user-select': 'none' /* Firefox */,
    '-ms-user-select': 'none' /* Internet Explorer/Edge */,
    'user-select': 'none',
  },
  card: {
    width: '100%',
    marginRight: theme.spacing(3),
    overflow: 'visible',
    padding: `${theme.spacing()}px !important`,
  },
  cardRoot: {
    marginBottom: 0,
  },
  secondLevelCategory: {
    border: `1px solid ${theme.colors.grayBlue}`,
    boxShadow: `0px 2px 4px 0px rgba(33, 33, 33, 0.07)`,
    marginRight: 0,
  },
  secondLevelCategoryHeader: {
    padding: `0 ${theme.spacing()}px 0 ${theme.spacing()}px !important`,
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  categoryCardContent: {
    padding: `0 ${theme.spacing()}px ${theme.spacing()}px ${theme.spacing()}px !important`,
  },
  header: {
    padding: theme.spacing(),
  },
  align: {
    display: 'flex',
    alignItems: 'center',
  },
  action: {
    alignSelf: 'flex-end',
    paddingRight: theme.spacing(),
  },
  removeCircleIcon: {
    height: theme.spacing(2),
    width: theme.spacing(2),
  },
  dragHandleIcon: {
    height: theme.spacing(2.5),
    width: theme.spacing(2.5),
    marginTop: theme.spacing(2),
    cursor: 'move',
    color: theme.colors.gray1,
  },
  attributeSelector: {
    zIndex: theme.zIndex.surface,
  },
  attributeSelectorRoot: {
    zIndex: 'auto',
  },
  attributesList: {
    fontSize: 16,
  },
  iconContainer: {
    marginRight: theme.spacing(),
    display: 'flex',
    flexDirection: 'column',
  },
  arrowIcon: {
    cursor: 'pointer',
    color: theme.colors.gray1,
    height: theme.spacing(2.5),
    width: theme.spacing(2.5),
  },
  icon: {
    color: theme.colors.gray1,
  },
});

export default withStyles(styles)(CategoryCard);
